<template>
  <div class="admin-columns-page p-4">
    <div class="max-w-6xl mx-auto">
      <!-- Заголовок -->
      <div class="flex align-items-center justify-content-between mb-4">
        <div class="flex align-items-center gap-3">
          <i class="pi pi-table text-3xl text-primary"></i>
          <div>
            <h1 class="m-0 text-2xl font-bold">Кастомные колонки</h1>
            <p class="m-0 text-surface-500">Управление динамическими полями товаров</p>
          </div>
        </div>
        <Button
          label="Добавить колонку"
          icon="pi pi-plus"
          @click="openCreateDialog"
        />
      </div>

      <!-- Таблица колонок -->
      <Card>
        <template #content>
          <DataTable
            :value="columns"
            :loading="loading"
            stripedRows
            dataKey="id"
            class="columns-table"
            responsiveLayout="scroll"
          >
            <template #empty>
              <div class="text-center p-4 text-surface-500">
                <i class="pi pi-inbox text-4xl mb-2"></i>
                <p>Нет кастомных колонок</p>
              </div>
            </template>

            <Column field="display_order" header="#" style="width: 60px">
              <template #body="{ data }">
                <span class="text-surface-400">{{ data.display_order }}</span>
              </template>
            </Column>

            <Column field="field_key" header="Ключ поля" style="min-width: 150px">
              <template #body="{ data }">
                <code class="text-sm bg-surface-100 p-1 border-round">{{ data.field_key }}</code>
              </template>
            </Column>

            <Column field="display_name" header="Название" style="min-width: 200px">
              <template #body="{ data }">
                <div>
                  <div class="font-medium">{{ data.display_name }}</div>
                  <div v-if="data.display_name_en" class="text-sm text-surface-400">{{ data.display_name_en }}</div>
                </div>
              </template>
            </Column>

            <Column field="data_type" header="Тип" style="width: 100px">
              <template #body="{ data }">
                <Tag :value="data.data_type" :severity="getTypeSeverity(data.data_type)" />
              </template>
            </Column>

            <Column field="group_name" header="Группа" style="width: 150px">
              <template #body="{ data }">
                <span v-if="data.group_name">{{ data.group_name }}</span>
                <span v-else class="text-surface-400">—</span>
              </template>
            </Column>

            <Column header="Опции" style="width: 150px">
              <template #body="{ data }">
                <div class="flex gap-1">
                  <Tag v-if="data.is_required" value="Обяз." severity="danger" class="text-xs" />
                  <Tag v-if="data.is_filterable" value="Фильтр" severity="info" class="text-xs" />
                  <Tag v-if="data.is_sortable" value="Сорт." severity="secondary" class="text-xs" />
                </div>
              </template>
            </Column>

            <Column field="is_active" header="Статус" style="width: 100px">
              <template #body="{ data }">
                <Tag
                  :value="data.is_active ? 'Активна' : 'Отключена'"
                  :severity="data.is_active ? 'success' : 'warning'"
                />
              </template>
            </Column>

            <Column header="Действия" style="width: 120px">
              <template #body="{ data }">
                <div class="flex gap-1">
                  <Button
                    icon="pi pi-pencil"
                    severity="secondary"
                    variant="text"
                    size="small"
                    v-tooltip.top="'Редактировать'"
                    @click="openEditDialog(data)"
                  />
                  <Button
                    icon="pi pi-trash"
                    severity="danger"
                    variant="text"
                    size="small"
                    v-tooltip.top="'Удалить'"
                    @click="confirmDelete(data)"
                  />
                </div>
              </template>
            </Column>
          </DataTable>
        </template>
      </Card>

      <!-- Информационная карточка -->
      <Card class="mt-4">
        <template #title>
          <div class="flex align-items-center gap-2">
            <i class="pi pi-info-circle"></i>
            <span>Информация</span>
          </div>
        </template>
        <template #content>
          <div class="grid">
            <div class="col-12 md:col-6">
              <h4 class="mt-0 mb-2">Типы данных</h4>
              <ul class="m-0 pl-4">
                <li><strong>string</strong> — текстовое поле</li>
                <li><strong>number</strong> — числовое поле</li>
                <li><strong>boolean</strong> — да/нет</li>
              </ul>
            </div>
            <div class="col-12 md:col-6">
              <h4 class="mt-0 mb-2">Ключ поля (field_key)</h4>
              <ul class="m-0 pl-4">
                <li>Только латиница, цифры и _</li>
                <li>Должен начинаться с буквы</li>
                <li>Пример: lens_width, collection_year</li>
              </ul>
            </div>
          </div>
        </template>
      </Card>
    </div>

    <!-- Диалог создания/редактирования -->
    <Dialog
      v-model:visible="dialogVisible"
      :header="editingColumn ? 'Редактирование колонки' : 'Новая колонка'"
      :modal="true"
      :style="{ width: '550px' }"
      :closable="!saving"
    >
      <div class="flex flex-column gap-4">
        <!-- Ключ поля (только при создании) -->
        <div v-if="!editingColumn" class="flex flex-column gap-2">
          <label for="field_key" class="font-medium">Ключ поля *</label>
          <InputText
            id="field_key"
            v-model="form.field_key"
            placeholder="lens_width"
            :class="{ 'p-invalid': formErrors.field_key }"
          />
          <small v-if="formErrors.field_key" class="p-error">{{ formErrors.field_key }}</small>
          <small class="text-surface-400">Только a-z, 0-9, _ (начинается с буквы)</small>
        </div>

        <!-- Название RU -->
        <div class="flex flex-column gap-2">
          <label for="display_name" class="font-medium">Название (RU) *</label>
          <InputText
            id="display_name"
            v-model="form.display_name"
            placeholder="Ширина линзы"
            :class="{ 'p-invalid': formErrors.display_name }"
          />
          <small v-if="formErrors.display_name" class="p-error">{{ formErrors.display_name }}</small>
        </div>

        <!-- Название EN -->
        <div class="flex flex-column gap-2">
          <label for="display_name_en" class="font-medium">Название (EN)</label>
          <InputText
            id="display_name_en"
            v-model="form.display_name_en"
            placeholder="Lens Width"
          />
        </div>

        <!-- Тип данных -->
        <div class="flex flex-column gap-2">
          <label for="data_type" class="font-medium">Тип данных *</label>
          <Select
            id="data_type"
            v-model="form.data_type"
            :options="dataTypeOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Выберите тип"
          />
        </div>

        <!-- Группа -->
        <div class="flex flex-column gap-2">
          <label for="group_name" class="font-medium">Группа</label>
          <InputText
            id="group_name"
            v-model="form.group_name"
            placeholder="Размеры"
          />
        </div>

        <!-- Опции -->
        <div class="flex flex-column gap-2">
          <label class="font-medium">Опции</label>
          <div class="flex flex-wrap gap-3">
            <div class="flex align-items-center gap-2">
              <Checkbox v-model="form.is_required" inputId="is_required" :binary="true" />
              <label for="is_required">Обязательное</label>
            </div>
            <div class="flex align-items-center gap-2">
              <Checkbox v-model="form.is_filterable" inputId="is_filterable" :binary="true" />
              <label for="is_filterable">Фильтрация</label>
            </div>
            <div class="flex align-items-center gap-2">
              <Checkbox v-model="form.is_sortable" inputId="is_sortable" :binary="true" />
              <label for="is_sortable">Сортировка</label>
            </div>
            <div class="flex align-items-center gap-2">
              <Checkbox v-model="form.is_active" inputId="is_active" :binary="true" />
              <label for="is_active">Активна</label>
            </div>
          </div>
        </div>

        <!-- Excel паттерны -->
        <div class="flex flex-column gap-2">
          <label for="excel_patterns" class="font-medium">Паттерны для Excel (через запятую)</label>
          <InputText
            id="excel_patterns"
            v-model="excelPatternsString"
            placeholder="ширина линзы, lens width, lens_width"
          />
          <small class="text-surface-400">Используются для автоматического маппинга при импорте Excel</small>
        </div>
      </div>

      <template #footer>
        <Button label="Отмена" severity="secondary" @click="dialogVisible = false" :disabled="saving" />
        <Button :label="editingColumn ? 'Сохранить' : 'Создать'" icon="pi pi-check" @click="saveColumn" :loading="saving" />
      </template>
    </Dialog>

    <!-- Диалог подтверждения удаления -->
    <Dialog
      v-model:visible="deleteDialogVisible"
      header="Подтверждение удаления"
      :modal="true"
      :style="{ width: '450px' }"
    >
      <div class="flex align-items-center gap-3">
        <i class="pi pi-exclamation-triangle text-4xl text-orange-500"></i>
        <div>
          <p class="m-0 mb-2">Вы уверены, что хотите удалить колонку <strong>{{ columnToDelete?.display_name }}</strong>?</p>
          <p class="m-0 text-sm text-surface-500">Данные в товарах сохранятся, но колонка не будет отображаться.</p>
        </div>
      </div>
      <template #footer>
        <Button label="Отмена" severity="secondary" @click="deleteDialogVisible = false" />
        <Button label="Удалить" severity="danger" icon="pi pi-trash" @click="deleteColumn" :loading="deleting" />
      </template>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useToast } from 'primevue/usetoast'
import Card from 'primevue/card'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import Tag from 'primevue/tag'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Select from 'primevue/select'
import Checkbox from 'primevue/checkbox'
import customColumnsService from '@/services/customColumnsService'
import { useProductSchemaStore } from '@/stores/productSchemaStore'

const toast = useToast()
const productSchemaStore = useProductSchemaStore()

// State
const columns = ref([])
const loading = ref(false)
const dialogVisible = ref(false)
const deleteDialogVisible = ref(false)
const saving = ref(false)
const deleting = ref(false)
const editingColumn = ref(null)
const columnToDelete = ref(null)

const form = ref({
  field_key: '',
  display_name: '',
  display_name_en: '',
  data_type: 'string',
  is_required: false,
  is_filterable: false,
  is_sortable: false,
  is_active: true,
  group_name: '',
  excel_patterns: []
})

const formErrors = ref({})

const dataTypeOptions = [
  { label: 'Текст (string)', value: 'string' },
  { label: 'Число (number)', value: 'number' },
  { label: 'Да/Нет (boolean)', value: 'boolean' }
]

// Computed
const excelPatternsString = computed({
  get: () => form.value.excel_patterns?.join(', ') || '',
  set: (val) => {
    form.value.excel_patterns = val
      ? val.split(',').map(p => p.trim()).filter(p => p)
      : []
  }
})

// Methods
const getTypeSeverity = (type) => {
  switch (type) {
    case 'string': return 'info'
    case 'number': return 'success'
    case 'boolean': return 'warning'
    default: return 'secondary'
  }
}

const loadColumns = async () => {
  loading.value = true
  try {
    const response = await customColumnsService.getAll(true) // Включаем неактивные
    columns.value = response.items
  } catch (error) {
    console.error('Error loading columns:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось загрузить список колонок',
      life: 5000
    })
  } finally {
    loading.value = false
  }
}

const resetForm = () => {
  form.value = {
    field_key: '',
    display_name: '',
    display_name_en: '',
    data_type: 'string',
    is_required: false,
    is_filterable: false,
    is_sortable: false,
    is_active: true,
    group_name: '',
    excel_patterns: []
  }
  formErrors.value = {}
}

const openCreateDialog = () => {
  editingColumn.value = null
  resetForm()
  dialogVisible.value = true
}

const openEditDialog = (column) => {
  editingColumn.value = column
  form.value = {
    display_name: column.display_name,
    display_name_en: column.display_name_en || '',
    data_type: column.data_type,
    is_required: column.is_required,
    is_filterable: column.is_filterable,
    is_sortable: column.is_sortable,
    is_active: column.is_active,
    group_name: column.group_name || '',
    excel_patterns: column.excel_patterns || []
  }
  formErrors.value = {}
  dialogVisible.value = true
}

const validateForm = () => {
  formErrors.value = {}

  if (!editingColumn.value) {
    if (!form.value.field_key) {
      formErrors.value.field_key = 'Ключ поля обязателен'
    } else if (!/^[a-z][a-z0-9_]*$/.test(form.value.field_key)) {
      formErrors.value.field_key = 'Только a-z, 0-9, _ (начинается с буквы)'
    }
  }

  if (!form.value.display_name) {
    formErrors.value.display_name = 'Название обязательно'
  }

  return Object.keys(formErrors.value).length === 0
}

const saveColumn = async () => {
  if (!validateForm()) return

  saving.value = true
  try {
    if (editingColumn.value) {
      // Обновление
      await customColumnsService.update(editingColumn.value.id, form.value)
      toast.add({
        severity: 'success',
        summary: 'Успешно',
        detail: 'Колонка обновлена',
        life: 3000
      })
    } else {
      // Создание
      await customColumnsService.create(form.value)
      toast.add({
        severity: 'success',
        summary: 'Успешно',
        detail: 'Колонка создана',
        life: 3000
      })
    }
    dialogVisible.value = false
    await loadColumns()
    // Сбрасываем кэш схемы — другие открытые компоненты подтянут обновлённый список.
    productSchemaStore.invalidate()
  } catch (error) {
    console.error('Error saving column:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error.response?.data?.detail || 'Не удалось сохранить колонку',
      life: 5000
    })
  } finally {
    saving.value = false
  }
}

const confirmDelete = (column) => {
  columnToDelete.value = column
  deleteDialogVisible.value = true
}

const deleteColumn = async () => {
  if (!columnToDelete.value) return

  deleting.value = true
  try {
    await customColumnsService.delete(columnToDelete.value.id)
    toast.add({
      severity: 'success',
      summary: 'Успешно',
      detail: 'Колонка удалена',
      life: 3000
    })
    deleteDialogVisible.value = false
    await loadColumns()
    productSchemaStore.invalidate()
  } catch (error) {
    console.error('Error deleting column:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error.response?.data?.detail || 'Не удалось удалить колонку',
      life: 5000
    })
  } finally {
    deleting.value = false
  }
}

// Lifecycle
onMounted(() => {
  loadColumns()
})
</script>

<style scoped>
.admin-columns-page {
  min-height: calc(100vh - 60px);
  background: var(--surface-ground);
}

.columns-table :deep(.p-datatable-tbody > tr) {
  transition: background-color 0.2s;
}

.columns-table :deep(.p-datatable-tbody > tr:hover) {
  background: var(--surface-50) !important;
}

code {
  font-family: 'Fira Code', 'Monaco', monospace;
}
</style>
